import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import TailwindWrapper from "../../../components/TailwindWrapper/TailwindWrapper";
import EMPChip from "../EMPChip/EMPChip";

const proximaRegularFontFamily =
  '"ProximaNovaRegular", "Lincoln Proxima Nova", Arial, sans-serif';

const proximaSemiboldFontFamily =
  '"ProximaNovaSemibold", "Lincoln Proxima Nova Semibold", Arial, sans-serif';

const tabContainerStyle = {
  display: "flex",
  padding: "var(--space-lg, 16px)",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch",
  borderRadius: "var(--radius-md, 8px)",
  border:
    "var(--border-width-xs, 1px) solid var(--color-stroke-onlight-subtle-dividers, #B2B2B2)",
  background: "var(--color-fill-onlight-highcontrast-default, #FFF)",
};

const titleStyle = {
  color: "var(--color-text-onlight-moderate-default, #333)",
  fontFamily: proximaSemiboldFontFamily,
  fontSize: "var(--body-1-semibold-font-size, 16px)",
  fontStyle: "normal",
  fontWeight: "var(--body-1-semibold-font-weight, 600)",
  lineHeight: "var(--body-1-semibold-line-height, 24px)",
};

const descriptionStyle = {
  color: "var(--color-text-onlight-moderate-default, #333)",
  fontFamily: proximaRegularFontFamily,
  fontSize: "var(--body-1-regular-font-size, 16px)",
  fontStyle: "normal",
  fontWeight: "var(--body-1-regular-font-weight, 400)",
  lineHeight: "var(--body-1-regular-line-height, 24px)",
};

const EMPStatusTab = ({
  id,
  title,
  description,
  hasStatus,
  onClick,
  isActive,
  disabled,
  customChipLabel,
  customChipLabelColor,
}) => {
  const disabledClasses = disabled
    ? "tw-opacity-90 tw-cursor-not-allowed"
    : "hover:tw-bg-gray-50 tw-cursor-pointer";

  const chipLabel =
    customChipLabel || (disabled ? "Coming August 2025" : "Action Required");

  const chipLabelColor =
    customChipLabelColor || (disabled ? "gray" : "warning");

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(id);
    }
  };

  const handleKeyPress = (e) => {
    if (!disabled && e.key === "Enter" && onClick) {
      onClick(id);
    }
  };

  const activeStyle = isActive ? { border: "1px solid #485B65" } : {};

  return (
    <TailwindWrapper>
      <Box
        className={disabledClasses}
        style={{ ...tabContainerStyle, ...activeStyle }}
        onClick={handleClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyPress}
        aria-disabled={disabled}
        aria-current={isActive ? "true" : undefined}
      >
        <Box className="tw-flex tw-w-full tw-items-center tw-justify-between">
          <Box className="tw-flex-grow">
            <Typography
              variant="body1"
              component="p"
              className="tw-m-0"
              style={titleStyle}
            >
              {title}
            </Typography>

            {hasStatus && (
              <Box className="tw-mt-1 tw-mb-1">
                <EMPChip labelColor={chipLabelColor} label={chipLabel} />
              </Box>
            )}

            <Typography
              variant="body1"
              component="p"
              className="tw-m-0"
              style={descriptionStyle}
            >
              {description}
            </Typography>
          </Box>

          <svg
            className={`tw-w-5 tw-h-5 tw-ml-4 tw-flex-shrink-0 ${
              disabled ? "tw-text-gray-300" : "tw-text-black"
            }`}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M9 5l7 7-7 7"></path>
          </svg>
        </Box>
      </Box>
    </TailwindWrapper>
  );
};

EMPStatusTab.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  hasStatus: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
  customChipLabel: PropTypes.string,
  customChipLabelColor: PropTypes.oneOf([
    "success",
    "warning",
    "info",
    "error",
    "gray",
  ]),
};

export default EMPStatusTab;
